import { productosDB, ventasDB, clientesDB, movimientosDB, usuariosDB, categoriasDB, configuracionDB } from './index'

const BACKUP_FORMAT = 'practicaja-encrypted-backup'
const PBKDF2_ITERATIONS = 250000

function bytesToBase64(bytes) {
  let binary = ''
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  }
  return btoa(binary)
}

function base64ToBytes(value) {
  const binary = atob(value)
  return Uint8Array.from(binary, char => char.charCodeAt(0))
}

async function deriveBackupKey(passphrase, salt, usage) {
  const material = await crypto.subtle.importKey(
    'raw', new TextEncoder().encode(passphrase), 'PBKDF2', false, ['deriveKey']
  )
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: PBKDF2_ITERATIONS },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    [usage]
  )
}

async function cifrarBackup(backup, passphrase) {
  if (!passphrase || passphrase.length < 8) throw new Error('La contrasena debe tener al menos 8 caracteres')
  const salt = crypto.getRandomValues(new Uint8Array(16))
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const key = await deriveBackupKey(passphrase, salt, 'encrypt')
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv }, key, new TextEncoder().encode(JSON.stringify(backup))
  )
  return {
    format: BACKUP_FORMAT,
    version: '2.0',
    kdf: { name: 'PBKDF2', hash: 'SHA-256', iterations: PBKDF2_ITERATIONS },
    salt: bytesToBase64(salt),
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
  }
}

async function descifrarBackup(envelope, passphrase) {
  if (!passphrase) throw new Error('Ingresa la contrasena del respaldo')
  try {
    const salt = base64ToBytes(envelope.salt)
    const iv = base64ToBytes(envelope.iv)
    const key = await deriveBackupKey(passphrase, salt, 'decrypt')
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv }, key, base64ToBytes(envelope.ciphertext)
    )
    return JSON.parse(new TextDecoder().decode(plaintext))
  } catch {
    throw new Error('Contrasena incorrecta o respaldo danado')
  }
}

async function leerTodos(db) {
  const result = await db.allDocs({ include_docs: true })
  return result.rows
    .map(r => r.doc)
    .filter(d => !d._id.startsWith('_design'))
    .map(({ _rev, ...doc }) => doc)   // quitar _rev para import limpio
}

export async function exportarDatos(passphrase) {
  const [productos, ventas, clientes, movimientos, usuarios, categorias, configuracion] = await Promise.all([
    leerTodos(productosDB),
    leerTodos(ventasDB),
    leerTodos(clientesDB),
    leerTodos(movimientosDB),
    leerTodos(usuariosDB),
    leerTodos(categoriasDB),
    leerTodos(configuracionDB),
  ])

  const backup = {
    version: '1.1',
    fecha: new Date().toISOString(),
    productos,
    ventas,
    clientes,
    movimientos,
    usuarios,
    categorias,
    configuracion,
  }

  const encrypted = await cifrarBackup(backup, passphrase)
  const blob = new Blob([JSON.stringify(encrypted)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  const fechaStr = new Date().toISOString().slice(0, 10)
  a.href = url
  a.download = `practicaja-backup-${fechaStr}.practicaja`
  a.click()
  URL.revokeObjectURL(url)
}

// Elimina todos los docs de una DB y luego inserta los del backup
async function limpiarYRestaurar(db, docs) {
  const result = await db.allDocs()
  const aEliminar = result.rows
    .filter(r => !r.id.startsWith('_design'))
    .map(r => ({ _id: r.id, _rev: r.value.rev, _deleted: true }))
  if (aEliminar.length > 0) await db.bulkDocs(aEliminar)
  if (docs.length > 0) await db.bulkDocs(docs)
}

export async function importarDatos(archivo, passphrase) {
  const texto = await archivo.text()
  const parsed = JSON.parse(texto)
  const backup = parsed.format === BACKUP_FORMAT
    ? await descifrarBackup(parsed, passphrase)
    : parsed

  if (!backup.productos || !backup.ventas || !backup.clientes) {
    throw new Error('Archivo de respaldo inválido')
  }

  await Promise.all([
    limpiarYRestaurar(productosDB,    backup.productos),
    limpiarYRestaurar(ventasDB,       backup.ventas),
    limpiarYRestaurar(clientesDB,     backup.clientes),
    limpiarYRestaurar(movimientosDB,  backup.movimientos   ?? []),
    limpiarYRestaurar(usuariosDB,     backup.usuarios      ?? []),
    limpiarYRestaurar(categoriasDB,   backup.categorias    ?? []),
    limpiarYRestaurar(configuracionDB,backup.configuracion ?? []),
  ])

  return {
    productos:  backup.productos.length,
    ventas:     backup.ventas.length,
    clientes:   backup.clientes.length,
    movimientos: (backup.movimientos ?? []).length,
  }
}
